import { useState } from "react";
import {
  Box,
  SimpleGrid,
  Text,
  Link,
  Icon,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverHeader,
  PopoverBody,
  PopoverCloseButton,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Button,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { FaEnvelope, FaGithub, FaLinkedin, FaWhatsapp } from "react-icons/fa";
import { useProfile } from "../hooks/useContent";
import { useLanguage } from "../context/LanguageContext";
import supabase from "../lib/supabaseClient";
import Section from "./ui/Section";

const CARD_STYLE = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 3,
  p: 6,
  bg: "bg.card",
  border: "1px solid",
  borderColor: "border.card",
  rounded: "xl",
  transition: "all 0.2s",
  _hover: { borderColor: "brand.400", transform: "translateY(-2px)" },
};

function CardInner({ icon, label }) {
  return (
    <>
      <Icon as={icon} boxSize={8} color="brand.400" />
      <Text fontSize="sm" color="text.muted">
        {label}
      </Text>
    </>
  );
}

function LinkCard({ href, icon, label }) {
  return (
    <Link
      href={href}
      isExternal
      rel="noopener noreferrer"
      aria-label={label}
      _hover={{ textDecoration: "none" }}
    >
      <Box {...CARD_STYLE}>
        <CardInner icon={icon} label={label} />
      </Box>
    </Link>
  );
}

function ContactForm({ onClose }) {
  const { language } = useLanguage();
  const toast = useToast();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const t = (es, en) => (language === "es" ? es : en);

  const isValid =
    form.name.trim() && form.email.trim() && form.message.trim();

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data, error } = await supabase.functions.invoke("send-contact", {
        body: form,
      });
      if (error || data?.error) {
        // On a non-2xx response supabase-js gives a generic message; read the
        // function's JSON body to surface the real reason when available.
        let detail = data?.error || error?.message;
        try {
          const body = await error?.context?.json();
          if (body?.error) detail = body.error;
        } catch {
          /* body not JSON — keep the generic message */
        }
        throw new Error(detail);
      }
      toast({
        title: t("¡Mensaje enviado!", "Message sent!"),
        description: t(
          "Gracias, te responderé pronto.",
          "Thanks, I'll get back to you soon."
        ),
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      setForm({ name: "", email: "", message: "" });
      onClose();
    } catch (err) {
      toast({
        title: t("No se pudo enviar", "Couldn't send"),
        description:
          err.message ||
          t("Inténtalo de nuevo más tarde.", "Please try again later."),
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Box display="flex" flexDirection="column" gap={3}>
        <FormControl isRequired>
          <FormLabel fontSize="sm" mb={1}>
            {t("Nombre", "Name")}
          </FormLabel>
          <Input
            name="name"
            size="sm"
            value={form.name}
            onChange={handleChange}
            maxLength={120}
            placeholder={t("Tu nombre", "Your name")}
          />
        </FormControl>
        <FormControl isRequired>
          <FormLabel fontSize="sm" mb={1}>
            {t("Correo", "Email")}
          </FormLabel>
          <Input
            name="email"
            type="email"
            size="sm"
            value={form.email}
            onChange={handleChange}
            maxLength={200}
            placeholder="tu@correo.com"
          />
        </FormControl>
        <FormControl isRequired>
          <FormLabel fontSize="sm" mb={1}>
            {t("Mensaje", "Message")}
          </FormLabel>
          <Textarea
            name="message"
            size="sm"
            value={form.message}
            onChange={handleChange}
            maxLength={5000}
            rows={4}
            placeholder={t("¿En qué puedo ayudarte?", "How can I help you?")}
          />
        </FormControl>
        <Button
          type="submit"
          colorScheme="blue"
          size="sm"
          w="full"
          mt={1}
          isLoading={sending}
          isDisabled={!isValid}
          loadingText={t("Enviando…", "Sending…")}
        >
          {t("Enviar", "Send")}
        </Button>
      </Box>
    </form>
  );
}

function EmailCard() {
  const { language } = useLanguage();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const t = (es, en) => (language === "es" ? es : en);

  return (
    <Popover
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
      placement="top"
      closeOnBlur={false}
    >
      <PopoverTrigger>
        <Box as="button" type="button" aria-label="Email" w="full" {...CARD_STYLE}>
          <CardInner icon={FaEnvelope} label="Email" />
        </Box>
      </PopoverTrigger>
      <PopoverContent
        bg="bg.card"
        borderColor="border.card"
        w={{ base: "18rem", sm: "22rem" }}
        _focus={{ boxShadow: "lg" }}
      >
        <PopoverArrow bg="bg.card" />
        <PopoverCloseButton />
        <PopoverHeader borderBottomWidth="1px" borderColor="border.card">
          {t("Escríbeme", "Write to me")}
        </PopoverHeader>
        <PopoverBody>
          <ContactForm onClose={onClose} />
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}

function Contact() {
  const { data: p } = useProfile();
  const { language } = useLanguage();

  if (!p) return null;

  const linkCards = [
    p.github && { href: p.github, icon: FaGithub, label: "GitHub" },
    p.linkedin && { href: p.linkedin, icon: FaLinkedin, label: "LinkedIn" },
    p.whatsapp && {
      href: `https://wa.me/${p.whatsapp}`,
      icon: FaWhatsapp,
      label: "WhatsApp",
    },
  ].filter(Boolean);

  return (
    <Section
      id="contact"
      eyebrow={language === "es" ? "CONTACTO" : "CONTACT"}
      title={language === "es" ? "Hablemos" : "Let's talk"}
    >
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
        <EmailCard />
        {linkCards.map((item) => (
          <LinkCard key={item.label} {...item} />
        ))}
      </SimpleGrid>
    </Section>
  );
}

export default Contact;
